import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { BsFillCheckCircleFill } from "react-icons/bs";
import { MdPending } from "react-icons/md";
import { useHistory } from "react-router-dom";
import axios from "axios";
import config from "../../config";
import { CAR_DATA_UPDATE_STEPS } from "../../constant";
import { syncAuthCredits } from "../../../utils/accountActivity";
import { getAuthToken, getAuthUserId } from "../../../utils/userIdentity";
import "./style.css";

const PHOTO_TARGET = 16;

function countPhotos(record = {}) {
  const lists = [
    record.interior_images_,
    record.exterior_images_,
    record.wheels_images_,
    record.treads_images_,
  ];
  return lists.reduce(
    (sum, list) => sum + (Array.isArray(list) ? list.length : 0),
    0
  );
}

/**
 * Dealers post-valuation checklist.
 * Order matches client intent: contact → vehicle → photos → appointment.
 * Appointment Start is hidden until the photos target is reached.
 */
export const CarSteps = (props) => {
  const {
    auth,
    step1,
    step2,
    step3,
    evaluation,
    evaluation_id_,
    totalpics,
    add_car_data,
    syncCredits,
  } = props;
  const history = useHistory();
  const [loading, setLoading] = useState(false);

  const evaluateId = evaluation_id_ || evaluation?.id;
  const photoCount = Number.isFinite(totalpics) ? totalpics : 0;
  const photosComplete = photoCount >= PHOTO_TARGET;
  const appointmentComplete = Boolean(step3);

  const refreshEvaluation = () => {
    if (!evaluateId) return;
    setLoading(true);
    axios
      .post(`${config.baseUrl}api/v1/single_evaluate_details`, {
        evaluate_id: evaluateId,
      })
      .then((response) => {
        const record = response?.data?.data?.[0];
        if (record) {
          add_car_data(record);
        }
        // Photos may debit credits in the pictures app — refresh wallet here.
        if (typeof syncCredits === "function") {
          syncCredits(auth);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!step1 && !evaluateId) {
      history.push("/dealers");
      return;
    }
    if (evaluateId) {
      refreshEvaluation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Refresh photos + appointment when returning from vehicle details.
  useEffect(() => {
    const unlisten = history.listen((location) => {
      if (location.pathname === "/dealers/carsteps" && evaluateId) {
        refreshEvaluation();
      }
    });
    return unlisten;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [evaluateId]);

  // Refresh wallet when returning from the pictures app.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      if (!evaluateId) return;
      if (typeof syncCredits === "function") {
        syncCredits(auth);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [auth, evaluateId, syncCredits]);

  const goAppointment = () => {
    if (!photosComplete && !appointmentComplete) {
      return;
    }
    history.push("/dealers/bookappointment");
  };

  return (
    <>
      <div className="showInterest_headingCont ">
        <div className="showInterest_heading">Complete your listing</div>
        <div className="showInterest_description">
          Finish vehicle details and photos first. Booking opens after photos
          are uploaded.
        </div>
      </div>

      <div className="container">
        {/* Contact */}
        <div className="row my-5">
          <div className="car_card_step">
            <div className="card_content col-8 ">
              <div className=" row d-flex">
                <h3>1. Contact Details</h3>
                <p>Your contact information</p>
                {step1 ? (
                  <span>
                    <BsFillCheckCircleFill size={15} className="cici" />
                    &nbsp;Complete
                  </span>
                ) : (
                  <span>
                    <MdPending size={15} className="cici" />
                    &nbsp;Pending
                  </span>
                )}
              </div>
            </div>
            <div className="col-4">
              {step1 ? (
                <button type="button" className="card_bton">
                  Done
                </button>
              ) : (
                <button type="button" className="card_bton_w" disabled>
                  Start
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Vehicle */}
        <div className="row my-5">
          <div className="car_card_step">
            <div className="card_content col-8 ">
              <div className=" row d-flex">
                <h3>2. Vehicle Details</h3>
                <p>Questions and postal address for the vehicle</p>
                {step2 ? (
                  <span>
                    <BsFillCheckCircleFill size={15} className="cici" />
                    &nbsp;Complete
                  </span>
                ) : (
                  <span>
                    <MdPending size={15} className="cici" />
                    &nbsp;Pending
                  </span>
                )}
              </div>
            </div>
            <div className="col-4">
              {step2 ? (
                <button type="button" className="card_bton">
                  Done
                </button>
              ) : (
                <button
                  type="button"
                  className="card_bton_w"
                  onClick={() => history.push("/dealers/carupdate")}
                >
                  Start
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Photos — before appointment */}
        <div className="row my-5">
          <div className="car_card_step">
            <div className="card_content col-8 ">
              <div className=" row d-flex">
                <h3>3. Photos</h3>
                <p>
                  Upload vehicle photos from the link in your email (mobile
                  recommended). Target: {PHOTO_TARGET} photos.
                </p>
                <span>
                  {photosComplete ? (
                    <BsFillCheckCircleFill size={15} className="cici" />
                  ) : (
                    <MdPending size={15} className="cici" />
                  )}
                  &nbsp;
                  {photoCount} of {PHOTO_TARGET} uploaded
                  {loading ? " · refreshing…" : ""}
                </span>
              </div>
            </div>
            <div className="col-4">
              {photosComplete ? (
                <button type="button" className="card_bton">
                  Done
                </button>
              ) : (
                <button
                  type="button"
                  className="card_bton_w"
                  onClick={refreshEvaluation}
                  disabled={loading || !evaluateId}
                >
                  Refresh
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Appointment — Start only after photos */}
        <div className="row my-5">
          <div className="car_card_step">
            <div className="card_content col-8 ">
              <div className=" row d-flex">
                <h3>4. Book Appointment</h3>
                <p>
                  {photosComplete || appointmentComplete
                    ? "Book your inspection appointment"
                    : "Complete photos first to unlock booking"}
                </p>
                {appointmentComplete ? (
                  <span>
                    <BsFillCheckCircleFill size={15} className="cici" />
                    &nbsp;Complete
                  </span>
                ) : (
                  <span>
                    <MdPending size={15} className="cici" />
                    &nbsp;
                    {photosComplete ? "Pending" : "Locked until photos complete"}
                  </span>
                )}
              </div>
            </div>
            <div className="col-4">
              {appointmentComplete ? (
                <button
                  type="button"
                  className="card_bton"
                  onClick={goAppointment}
                >
                  Edit
                </button>
              ) : photosComplete ? (
                <button
                  type="button"
                  className="card_bton_w"
                  onClick={goAppointment}
                >
                  Start
                </button>
              ) : (
                <button type="button" className="card_bton_w" disabled>
                  Start
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  step1: state.sellMyCar.step1complete,
  step2: state.cardeailupdate.step2complete,
  step3: state.sellMyCar.step3complete,
  evaluation: state.sellMyCar.evaluation_create,
  evaluation_id_: state.sellMyCar.evaluation_create.id,
  totalpics: state.sellMyCar.totalpics,
});

const mapDispatchToProps = (dispatch) => ({
  add_car_data: (data) =>
    dispatch({
      type: CAR_DATA_UPDATE_STEPS,
      payload: { data },
    }),
  syncCredits: (auth) =>
    syncAuthCredits({
      userId: getAuthUserId(auth),
      token: getAuthToken(auth),
      dispatch,
    }),
});

export default connect(mapStateToProps, mapDispatchToProps)(CarSteps);
